// Copyright (c) Docugami, Inc. All rights reserved.

import { createApi } from '@reduxjs/toolkit/query/react';
import { Config } from 'config';
import { createDgBaseQuery } from 'store/baseQuery';

export interface DiscoveryEntity {
    name: string;
    entities: string[]; // individual entity values not shown on list page
}

export enum DiscoveryType {
    Classification = 'Classification',
    Entity = 'Entity'
}

export enum DiscoveryAssignmentType {
    Hold = 'Hold',
    Transition = 'Transition',
    Assign = 'Assign'
}

export interface DiscoverySet {
    id: string;
    name: string;
    type: DiscoveryType;
    description?: string;
    entities?: DiscoveryEntity[];
    assignmentType: DiscoveryAssignmentType;
    docsetId?: string;
    numDocumentsMatched?: number; // newly added count of matched documents
}

// Canonical response wrapper returned by backend
export interface DiscoverySetsResponse {
    discoverySets: DiscoverySet[];
}

// Creation request payload expected by backend
export interface DiscoverySetCreationRequest {
    name: string;
    type: DiscoveryType;
    description?: string;
    assignmentType: DiscoveryAssignmentType;
    docsetId?: string;
    entities?: DiscoveryEntity[];
}

// Generate request payload
export interface GenerateDiscoverySetsRequest {
    prompt: string;
}

export interface DiscoverySetUpdateRequest {
    name: string;
    type: DiscoveryType;
    description?: string;
    assignmentType: DiscoveryAssignmentType;
    docsetId?: string;
    entities?: DiscoveryEntity[];
}

// Document response types for getDiscoverySetDocuments
export interface DiscoveryDocumentDto {
    id: string;
    url: string;
    downloadUrl?: string;
    name: string;
    status: string;
    createdAt: string;
    size?: number;
    processedAt?: string;
    pageCount?: number;
}

export interface DiscoveryDocumentsResponse {
    documents: DiscoveryDocumentDto[];
    next?: string;
}

// Discovery run status response from backend
export interface DiscoveryRunStatusResponse {
    runId: string;
    status: string;
    commitStatus?: string;
    startedAt?: string;
    completedAt?: string;
    totalDocumentsAssigned: number;
    totalDocumentsConsidered: number;
    percentAssigned?: number; // already a percentage 0-100 from backend
}

const baseQuery = createDgBaseQuery(`${Config.publicApiBaseUrl}/discovery`, {
    paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const key in params) {
            const value = params[key];
            if (value !== undefined) {
                if (Array.isArray(value)) {
                    value.forEach((val) => searchParams.append(key, val));
                } else {
                    searchParams.append(key, value);
                }
            }
        }
        return searchParams.toString();
    },
});

// API slice for discovery sets
export const discoveryApi = createApi({
    reducerPath: 'discoveryApi',
    baseQuery: baseQuery,
    tagTypes: ['discovery-sets'],
    endpoints: builder => ({
        getDiscoverySets: builder.query<DiscoverySetsResponse, void>({
            query: () => '',
            providesTags: (result) => [{ type: 'discovery-sets', id: 'LIST' }]
        }),
        generateDiscoverySets: builder.mutation<DiscoverySet[], GenerateDiscoverySetsRequest>({
            query: (body) => ({
                url: '/generate',
                method: 'POST',
                body
            }),
            transformResponse: (response: any): DiscoverySet[] => {
                return response.discoverySets?.map((discoverySet: any) => ({
                    id: discoverySet.id,
                    name: discoverySet.name,
                    type: discoverySet.type as DiscoveryType,
                    description: discoverySet.description,
                    entities: discoverySet.entities?.map((e: any) => ({ name: e.name, entities: e.entities })) ?? [],
                    assignmentType: discoverySet.assignmentType as DiscoveryAssignmentType,
                    targetDocsetIds: discoverySet.targetClusterIds,
                    numDocumentsMatched: discoverySet.numDocumentsMatched
                }))
            },
            invalidatesTags: [{ type: 'discovery-sets', id: 'LIST' }]
        }),
        createDiscoverySet: builder.mutation<DiscoverySet, DiscoverySetCreationRequest>({
            query: (body) => ({
                url: '',
                method: 'POST',
                body
            }),
            transformResponse: (response: any): DiscoverySet => {
                // Map backend DiscoverySetResponse to DiscoverySetDto
                return {
                    id: response.id,
                    name: response.name,
                    type: response.type as DiscoveryType,
                    description: response.description,
                    entities: response.entities?.map((e: any) => ({ name: e.name, entities: e.entities })) ?? [],
                    assignmentType: response.assignmentType as DiscoveryAssignmentType,
                    docsetId: response.docsetId,
                    numDocumentsMatched: response.numDocumentsMatched
                };
            },
            invalidatesTags: [{ type: 'discovery-sets', id: 'LIST' }]
        }),
        updateDiscoverySet: builder.mutation<DiscoverySet, { id: string; body: DiscoverySetUpdateRequest }>({
            query: ({ id, body }) => ({
                url: `/${id}`,
                method: 'PUT',
                body
            }),
            transformResponse: (response: any): DiscoverySet => {
                return {
                    id: response.id,
                    name: response.name,
                    type: response.type as DiscoveryType,
                    description: response.description,
                    entities: response.entities?.map((e: any) => ({ name: e.name, entities: e.entities })) ?? [],
                    assignmentType: response.assignmentType as DiscoveryAssignmentType,
                    docsetId: response.docsetId,
                    numDocumentsMatched: response.numDocumentsMatched
                };
            },
            invalidatesTags: [{ type: 'discovery-sets', id: 'LIST' }]
        }),
        deleteDiscoverySet: builder.mutation<void, string>({
            query: (id) => ({
                url: `/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: [{ type: 'discovery-sets', id: 'LIST' }]
        }),
        getDiscoverySetDocuments: builder.query<DiscoveryDocumentsResponse, { id: string; limit?: number; cursor?: string }>({
            query: ({ id, limit, cursor }) => {
                const params = new URLSearchParams();
                if (limit !== undefined) params.append('limit', limit.toString());
                if (cursor) params.append('cursor', cursor);
                const queryString = params.toString();
                return `/discovery/${id}/documents${queryString ? `?${queryString}` : ''}`;
            }
        }),
        startDiscoveryRun: builder.mutation<DiscoveryRunStatusResponse, void>({
            query: () => ({
                url: '/start',
                method: 'POST'
            }),
            transformResponse: (response: any): DiscoveryRunStatusResponse => ({
                runId: response.runId,
                status: response.status,
                commitStatus: response.commitStatus,
                startedAt: response.startedAt,
                completedAt: response.completedAt,
                totalDocumentsAssigned: response.totalDocumentsAssigned,
                totalDocumentsConsidered: response.totalDocumentsConsidered,
                percentAssigned: response.percentAssigned
            })
        }),
        stopDiscoveryRun: builder.mutation<DiscoveryRunStatusResponse, string>({
            query: (runId) => ({
                url: `/stop?runId=${encodeURIComponent(runId)}`,
                method: 'POST'
            }),
            transformResponse: (response: any): DiscoveryRunStatusResponse => ({
                runId: response.runId,
                status: response.status,
                commitStatus: response.commitStatus,
                startedAt: response.startedAt,
                completedAt: response.completedAt,
                totalDocumentsAssigned: response.totalDocumentsAssigned,
                totalDocumentsConsidered: response.totalDocumentsConsidered,
                percentAssigned: response.percentAssigned
            })
        }),
        getDiscoveryRunStatus: builder.query<DiscoveryRunStatusResponse, string | undefined>({
            query: (runId) => ({
                url: `/status${runId ? `?runId=${encodeURIComponent(runId)}` : ''}`,
                method: 'POST'
            }),
            transformResponse: (response: any): DiscoveryRunStatusResponse => ({
                runId: response.runId,
                status: response.status,
                commitStatus: response.commitStatus,
                startedAt: response.startedAt,
                completedAt: response.completedAt,
                totalDocumentsAssigned: response.totalDocumentsAssigned,
                totalDocumentsConsidered: response.totalDocumentsConsidered,
                percentAssigned: response.percentAssigned
            })
        })
    })
});

export const {
    useGetDiscoverySetsQuery,
    useGenerateDiscoverySetsMutation,
    useCreateDiscoverySetMutation,
    useUpdateDiscoverySetMutation,
    useDeleteDiscoverySetMutation,
    useGetDiscoverySetDocumentsQuery,
    useLazyGetDiscoverySetDocumentsQuery,
    useStartDiscoveryRunMutation,
    useStopDiscoveryRunMutation,
    useGetDiscoveryRunStatusQuery
} = discoveryApi;
